import { useCallback, useRef } from "react";
import { Citation } from "../models/audit/citation";
import { useCitationNavigation } from "./useCitationNavigation";

interface UseCitationSelectionProps {
  citations: Citation[];
  /** Shows a citation in the document viewer. Called once per selection,
   * whether it came from `selectCitation`, an arrow key, or an index change. */
  displayCitation: (citation: Citation) => void;
  onKeyboardStep?: (citation: Citation) => void;
}

/**
 * Adds `selectCitation` on top of `useCitationNavigation`: it moves the active
 * index to the picked citation and displays it right away. Displaying it in
 * the same render matters when the pick also switches tabs, and it is the only
 * way a pick at the current index is shown at all. When the index does move,
 * the navigation hook's index effect would display the same citation a second
 * time; that update is skipped.
 */
export function useCitationSelection({
  citations,
  displayCitation,
  onKeyboardStep,
}: UseCitationSelectionProps) {
  const displayCitationRef = useRef(displayCitation);
  displayCitationRef.current = displayCitation;
  // Citation id whose index-effect update is redundant: `selectCitation` already displayed it.
  const skipIndexSelectIdRef = useRef<string | undefined>(undefined);

  const navigation = useCitationNavigation({
    filteredCitations: citations,
    onCitationSelect: (citation) => {
      const skip = skipIndexSelectIdRef.current === citation.id;
      skipIndexSelectIdRef.current = undefined;
      if (!skip) displayCitationRef.current(citation);
    },
    onKeyboardStep,
  });

  const { activeEvidenceIndex, setActiveEvidenceIndex } = navigation;
  const selectCitation = useCallback(
    (citation: Citation) => {
      const idx = citations.findIndex((c) => c.id === citation.id);
      if (idx >= 0 && idx !== activeEvidenceIndex) {
        skipIndexSelectIdRef.current = citation.id;
        setActiveEvidenceIndex(idx);
      }
      displayCitationRef.current(citation);
    },
    [citations, activeEvidenceIndex, setActiveEvidenceIndex],
  );

  return { navigation, selectCitation };
}
